// src/screens/internships/InternshipsScreen.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { collection, addDoc, query, where, getDocs, serverTimestamp, doc, getDoc } from "firebase/firestore";
import { auth, db } from "../../firebase";
import Sidebar from "../../components/dashboard/Sidebar";

const FALLBACK_INTERNSHIPS = [
  {
    id: "fullstack-ai",
    title: "Full Stack with AI",
    category: "Development",
    duration: "6 Months",
    stipend: "8,000 / month",
    seats: 30,
    badge: "Trending",
    badgeColor: "#F59E0B",
    badgeBg: "#FEF3C7",
    gradient: "linear-gradient(135deg, #7C3AED 0%, #A78BFA 100%)",
    accent: "#7C3AED",
    description: "Master modern full-stack development combined with AI integrations. Build real-world applications using React, Node.js, and cutting-edge AI APIs (OpenAI, Gemini). Gain hands-on experience in deploying AI-powered web apps.",
    skills: ["React", "Node.js", "Python", "OpenAI API", "MongoDB", "Docker"],
    emoji: "💻",
  },
  {
    id: "ai-ml",
    title: "AI & Machine Learning",
    category: "Artificial Intelligence",
    duration: "6 Months",
    stipend: "10,000 / month",
    seats: 20,
    badge: "Most Popular",
    badgeColor: "#6D28D9",
    badgeBg: "#EDE9FE",
    gradient: "linear-gradient(135deg, #5B21B6 0%, #8B5CF6 100%)",
    accent: "#5B21B6",
    description: "Dive deep into Machine Learning, Deep Learning, and Generative AI. Work on real datasets, build predictive models, neural networks, and LLM-based projects under expert mentorship.",
    skills: ["Python", "TensorFlow", "PyTorch", "Scikit-learn", "NLP", "LLMs"],
    emoji: "🤖",
  },
  {
    id: "cyber-security",
    title: "Cyber Security",
    category: "Security",
    duration: "6 Months",
    stipend: "9,000 / month",
    seats: 15,
    badge: "High Demand",
    badgeColor: "#4C1D95",
    badgeBg: "#F5F3FF",
    gradient: "linear-gradient(135deg, #4C1D95 0%, #7C3AED 100%)",
    accent: "#4C1D95",
    description: "Learn ethical hacking, penetration testing, network security, and digital forensics. Get certified-ready with hands-on labs covering OWASP, SOC operations, and incident response.",
    skills: ["Kali Linux", "Wireshark", "Metasploit", "OWASP", "Nmap", "Python"],
    emoji: "🛡️",
  },
  {
    id: "test-dhakshatha",
    title: "Dhakshatha Patel C",
    category: "Testing",
    duration: "1 Month",
    stipend: "10 / month",
    seats: 5,
    badge: "Test",
    badgeColor: "#DC2626",
    badgeBg: "#FEF2F2",
    gradient: "linear-gradient(135deg, #EF4444 0%, #F87171 100%)",
    accent: "#EF4444",
    description: "Testing internship for Dhakshatha Patel C.",
    skills: ["Test"],
    emoji: "??",
  },
  {
    id: "test-kallesh-dr",
    title: "Kallesh D R",
    category: "Testing",
    duration: "1 Month",
    stipend: "10 / month",
    seats: 5,
    badge: "Test",
    badgeColor: "#DC2626",
    badgeBg: "#FEF2F2",
    gradient: "linear-gradient(135deg, #EF4444 0%, #F87171 100%)",
    accent: "#EF4444",
    description: "Testing internship for Kallesh D R.",
    skills: ["Test"],
    emoji: "??",
  },
  {
    id: "test-geeta",
    title: "Geeta D C",
    category: "Testing",
    duration: "1 Month",
    stipend: "10 / month",
    seats: 5,
    badge: "Test",
    badgeColor: "#DC2626",
    badgeBg: "#FEF2F2",
    gradient: "linear-gradient(135deg, #EF4444 0%, #F87171 100%)",
    accent: "#EF4444",
    description: "Testing internship for Geeta D C.",
    skills: ["Test"],
    emoji: "??",
  },
  {
    id: "test-kallesh-dc",
    title: "Kallesh D C",
    category: "Testing",
    duration: "1 Month",
    stipend: "10 / month",
    seats: 5,
    badge: "Test",
    badgeColor: "#DC2626",
    badgeBg: "#FEF2F2",
    gradient: "linear-gradient(135deg, #EF4444 0%, #F87171 100%)",
    accent: "#EF4444",
    description: "Testing internship for Kallesh D C.",
    skills: ["Test"],
    emoji: "??",
  },
];

const InternshipsScreen = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);
  const [applying, setApplying] = useState({});
  const [appliedMap, setAppliedMap] = useState({});
  const [toast, setToast] = useState(null);
  const [internships, setInternships] = useState([]);
  const [expandedDesc, setExpandedDesc] = useState({});

  useEffect(() => {
    // Load internships from Firestore (admin-managed)
    const loadInternships = async () => {
      try {
        const snap = await getDocs(collection(db, "internshipdomains"));
        if (!snap.empty) {
          let loaded = snap.docs.map(d => ({
            id: d.id,
            ...d.data(),
            gradient: d.data().gradient || "linear-gradient(135deg, #7C3AED 0%, #A78BFA 100%)",
            accent: d.data().accent || "#7C3AED",
            badgeColor: d.data().badgeColor || "#6D28D9",
            badgeBg: d.data().badgeBg || "#EDE9FE",
          }));
          loaded.sort((a, b) => {
            const aTrend = a.badge?.toLowerCase() === "trending" ? -1 : 1;
            const bTrend = b.badge?.toLowerCase() === "trending" ? -1 : 1;
            return aTrend - bTrend;
          });
          setInternships(loaded);
        } else {
          setInternships(FALLBACK_INTERNSHIPS);
        }
      } catch (e) {
        console.error(e);
        setInternships(FALLBACK_INTERNSHIPS);
      }
    };
    loadInternships();

    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) { navigate("/login"); return; }
      setUser(u);
      try {
        const snap = await getDoc(doc(db, "internshipusers", u.uid));
        if (snap.exists()) setUserData(snap.data());
      } catch (e) { console.error(e); }
      setLoading(false);
    });
    return () => unsub();
  }, [navigate]);

  useEffect(() => {
    if (!user) return;
    const fetchApplications = async () => {
      try {
        const q = query(collection(db, "internshipapplications"), where("userId", "==", user.uid));
        const snap = await getDocs(q);
        const map = {};
        snap.forEach((d) => {
          const data = d.data();
          const appliedAt = data.appliedAt?.toDate ? data.appliedAt.toDate() : new Date(data.appliedAt);
          const canApplyOn = new Date(appliedAt);
          canApplyOn.setMonth(canApplyOn.getMonth() + 6);
          map[data.internshipId] = { applied: true, canApplyOn, status: data.status };
        });
        setAppliedMap(map);
      } catch (e) { console.error(e); }
    };
    fetchApplications();
  }, [user]);

  const handleApply = async (internship) => {
    if (!user) return;
    const existing = appliedMap[internship.id];
    if (existing?.applied) {
      const now = new Date();
      if (now < existing.canApplyOn) {
        const fmt = existing.canApplyOn.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
        showToast(`You can re-apply for "${internship.title}" after ${fmt}.`, "warn");
        return;
      }
    }
    setApplying((p) => ({ ...p, [internship.id]: true }));
    try {
      await addDoc(collection(db, "internshipapplications"), {
        userId: user.uid,
        userName: userData ? `${userData.firstName} ${userData.lastName}` : user.email,
        userEmail: user.email,
        internshipId: internship.id,
        internshipTitle: internship.title,
        internshipCategory: internship.category,
        duration: internship.duration,
        stipend: internship.stipend,
        status: "Applied",
        paymentStatus: "Pending",
        appliedAt: serverTimestamp(),
      });
      setAppliedMap((p) => {
        const can = new Date();
        can.setMonth(can.getMonth() + 6);
        return { ...p, [internship.id]: { applied: true, canApplyOn: can, status: "Applied" } };
      });
      showToast(`Successfully applied for "${internship.title}"!`, "success");
    } catch (e) {
      console.error(e);
      showToast("Failed to apply. Please try again.", "error");
    } finally {
      setApplying((p) => ({ ...p, [internship.id]: false }));
    }
  };

  const showToast = (msg, type) => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4500);
  };

  if (loading) return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "linear-gradient(135deg,#F5F3FF,#EDE9FE,#DDD6FE)" }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ width: 52, height: 52, borderRadius: "50%", border: "4px solid #EDE9FE", borderTopColor: "#7C3AED", animation: "spin 0.8s linear infinite", margin: "0 auto 16px" }} />
        <p style={{ color: "#7C3AED", fontWeight: 600, fontFamily: "Outfit,sans-serif" }}>Loading internships...</p>
      </div>
    </div>
  );

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "linear-gradient(135deg,#F5F3FF 0%,#EDE9FE 50%,#F5F3FF 100%)", fontFamily: "Inter,sans-serif" }}>
      <style>{`
        @keyframes fadeInUp { from{opacity:0;transform:translateY(20px);}to{opacity:1;transform:translateY(0);} }
        @keyframes spin { to{transform:rotate(360deg);} }
        @keyframes slideIn { from{opacity:0;transform:translateX(40px);}to{opacity:1;transform:translateX(0);} }
        ::-webkit-scrollbar{width:6px;} ::-webkit-scrollbar-thumb{background:rgba(139,92,246,0.3);border-radius:3px;}
        .intern-card:hover { transform:translateY(-4px) !important; box-shadow:0 20px 56px rgba(109,40,217,0.16) !important; }
        .apply-btn:hover:not(:disabled) { transform:translateY(-2px) !important; }
      `}</style>

      <Sidebar user={user} userData={userData} collapsed={collapsed} setCollapsed={setCollapsed} />

      <main style={{ flex: 1, overflowY: "auto", padding: "32px 36px 48px 28px", minWidth: 0 }}>

        {toast && (
          <div style={{
            position: "fixed", top: 24, right: 28, zIndex: 9999,
            background: "rgba(255,255,255,0.97)",
            border: `2px solid ${toast.type === "success" ? "#A78BFA" : toast.type === "warn" ? "#F59E0B" : "#EF4444"}`,
            borderRadius: 16, padding: "14px 22px", boxShadow: "0 16px 48px rgba(109,40,217,0.18)",
            fontSize: 13, fontWeight: 600, color: "#4C1D95", animation: "slideIn 0.3s ease", maxWidth: 360,
          }}>
            {toast.type === "success" ? "✅ " : toast.type === "warn" ? "⏳ " : "❌ "}{toast.msg}
          </div>
        )}

        {/* Header */}
        <div style={{ marginBottom: 34, animation: "fadeInUp 0.4s ease both" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 6 }}>
            <div style={{ width: 52, height: 52, borderRadius: 16, background: "linear-gradient(135deg,#7C3AED,#A78BFA)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 6px 20px rgba(124,58,237,0.32)" }}>
              <svg width="26" height="26" fill="none" viewBox="0 0 24 24" stroke="#fff" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <h1 style={{ margin: 0, fontSize: 27, fontFamily: "Outfit,sans-serif", fontWeight: 800, color: "#4C1D95" }}>Available Internships</h1>
              <p style={{ margin: 0, fontSize: 13, color: "#8B5CF6", fontWeight: 500, marginTop: 2 }}>Kickstart your career with our curated 6-month programs</p>
            </div>
          </div>
        </div>

        {/* Cards */}
        <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>
          {internships.map((intern, idx) => {
            const appInfo = appliedMap[intern.id];
            const isApplied = !!appInfo?.applied;
            const canReapply = isApplied && new Date() >= appInfo?.canApplyOn;
            const isApplying = !!applying[intern.id];

            return (
              <div key={intern.id} className="intern-card" style={{
                borderRadius: 24, overflow: "hidden",
                background: "rgba(255,255,255,0.90)", backdropFilter: "blur(20px)",
                border: "1.5px solid rgba(196,181,253,0.45)",
                boxShadow: "0 8px 40px rgba(109,40,217,0.10)",
                transition: "transform 0.3s, box-shadow 0.3s",
                animation: `fadeInUp 0.5s ${0.12 * idx}s ease both`,
              }}>
                {/* Top color bar */}
                <div style={{ height: 5, background: intern.gradient }} />

                <div style={{ padding: "30px 34px" }}>
                  {/* Title row */}
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 18, marginBottom: 14 }}>
                    <div style={{ width: 66, height: 66, borderRadius: 20, background: intern.gradient, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 30, flexShrink: 0, boxShadow: `0 6px 20px ${intern.accent}44` }}>
                      {intern.emoji}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 4 }}>
                        <h2 style={{ margin: 0, fontSize: 21, fontFamily: "Outfit,sans-serif", fontWeight: 800, color: "#4C1D95" }}>{intern.title}</h2>
                        <span style={{ background: intern.badgeBg, color: intern.badgeColor, borderRadius: 8, fontSize: 11, fontWeight: 700, padding: "3px 11px" }}>{intern.badge}</span>
                        {isApplied && !canReapply && <span style={{ background: "#D1FAE5", color: "#065F46", borderRadius: 8, fontSize: 11, fontWeight: 700, padding: "3px 11px" }}>✓ Applied</span>}
                      </div>
                      <div style={{ fontSize: 12, color: "#8B5CF6", fontWeight: 500 }}>{intern.category} &bull; {intern.duration}</div>
                    </div>
                  </div>

                  {/* Description */}
                  <div style={{ margin: "0 0 16px", fontSize: 13.5, color: "#6B7280", lineHeight: 1.7 }}>
                    <div style={expandedDesc[intern.id] ? {} : { display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                      {intern.description}
                    </div>
                    {intern.description?.length > 120 && (
                      <button 
                        onClick={() => setExpandedDesc(p => ({...p, [intern.id]: !p[intern.id]}))}
                        style={{ color: "#7C3AED", fontWeight: 600, background: "none", border: "none", padding: 0, cursor: "pointer", marginTop: 4, display: "inline-block" }}
                      >
                        {expandedDesc[intern.id] ? "Show Less" : "Read More"}
                      </button>
                    )}
                  </div>

                  {/* Skills */}
                  <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginBottom: 20 }}>
                    {intern.skills.map(s => (
                      <span key={s} style={{ background: "rgba(196,181,253,0.22)", color: "#5B21B6", borderRadius: 8, padding: "4px 11px", fontSize: 11, fontWeight: 600, border: "1px solid rgba(167,139,250,0.3)" }}>{s}</span>
                    ))}
                  </div>

                  {/* Meta */}
                  <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 24 }}>
                    {[
                      intern.stipend ? { icon: "💰", label: "Stipend", val: `₹${intern.stipend}` } : null,
                      { icon: "💺", label: "Seats", val: `${intern.seats} left` },
                      { icon: "📅", label: "Duration", val: intern.duration },
                    ].filter(Boolean).map(m => (
                      <div key={m.label} style={{ display: "flex", alignItems: "center", gap: 8, background: "rgba(237,233,254,0.6)", borderRadius: 12, padding: "9px 16px" }}>
                        <span style={{ fontSize: 16 }}>{m.icon}</span>
                        <div>
                          <div style={{ fontSize: 9, color: "#A78BFA", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em" }}>{m.label}</div>
                          <div style={{ fontSize: 13, color: "#4C1D95", fontWeight: 700 }}>{m.val}</div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* CTA */}
                  <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
                    {isApplied && !canReapply ? (
                      <>
                        <button disabled style={{ padding: "13px 28px", borderRadius: 14, border: "none", background: "rgba(196,181,253,0.3)", color: "#A78BFA", fontSize: 14, fontWeight: 700, cursor: "not-allowed" }}>
                          Already Applied
                        </button>
                        <button onClick={() => navigate("/dashboard/my-applications")} style={{ padding: "13px 24px", borderRadius: 14, border: "1.5px solid rgba(167,139,250,0.7)", background: "transparent", color: "#7C3AED", fontSize: 13, fontWeight: 600, cursor: "pointer", transition: "background 0.2s" }}
                          onMouseEnter={e => e.currentTarget.style.background = "rgba(237,233,254,0.8)"}
                          onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                        >
                          View My Application →
                        </button>
                        {appInfo?.canApplyOn && (
                          <span style={{ fontSize: 11, color: "#A78BFA", fontWeight: 500 }}>
                            Re-apply after {appInfo.canApplyOn.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                          </span>
                        )}
                      </>
                    ) : (
                      <button className="apply-btn" onClick={() => handleApply(intern)} disabled={isApplying} style={{
                        padding: "13px 34px", borderRadius: 14, border: "none",
                        background: isApplying ? "rgba(196,181,253,0.5)" : intern.gradient,
                        color: "#fff", fontSize: 15, fontWeight: 700, cursor: isApplying ? "not-allowed" : "pointer",
                        boxShadow: isApplying ? "none" : `0 8px 24px ${intern.accent}44`,
                        transition: "transform 0.2s, box-shadow 0.2s",
                        display: "flex", alignItems: "center", gap: 8,
                      }}>
                        {isApplying ? (
                          <><div style={{ width: 16, height: 16, borderRadius: "50%", border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "#fff", animation: "spin 0.7s linear infinite" }} />Applying...</>
                        ) : (
                          <>Apply Now <span style={{ fontSize: 18 }}>→</span></>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
};

export default InternshipsScreen;
